import { StyleSheet, View, ViewProps } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, Draft, ProfileData, useProfile, useProfileMutation, useStore, useUserMutation } from 'zens-store'
import { Color, Radius, Spacing } from 'zens-storybook'
import { useState } from 'react'
import { useRouter, useParams, useNumberParams } from 'zens-router'
import { NavigationDelete, PageHeader, ProfileForm } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

interface Props extends ViewProps {}

const ProfileDetail = (props: Props) => {
  const { style, ...otherProps } = props
  const [errors, setErrors] = useState<Record<string, string>>()
  const [confirmDelete, setConfirmDelete] = useState<Draft<ProfileData>>()
  const router = useRouter()
  const { message, i18n } = useStore()
  const { t } = useTranslation()
  const [{ next }] = useParams()
  const [{ companyId }] = useNumberParams()
  const profile = useProfile()
  const profileMutation = useProfileMutation()
  const userMutation = useUserMutation({ companyId })

  const save = async (draft: Draft<ProfileData>) => {
    try {
      setErrors({})
      await profileMutation.update(draft)
      if (draft.data.language && draft.data.language !== profile.data?.language) {
        i18n.changeLanguage(draft.data.language)
      }
      message.add({
        message: t('Profile was updated successfully'),
        status: 'success',
      })
      showNext()
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`PROFILE: Profile failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Profile could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
    }
  }

  const showNext = async () => {
    router.push(next)
  }

  const remove = async () => {
    try {
      await userMutation.delete(confirmDelete!, companyId)
      message.add({
        message: t('Profile was deleted successfully'),
        status: 'success',
      })
      router.push('/email')
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`PROFILE: Profile deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Profile could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <PageHeader title={t('Profile and preferences')} />
      {profile.data && (
        <View style={styles.content}>
          <ProfileForm
            data={profile.data?.data}
            onSave={save}
            onCancel={showNext}
            onDelete={async (draft) => setConfirmDelete(draft)}
            onEmail={() => router.push({ pathname: `/profile/email`, params: { companyId, next } })}
            onPassword={() => router.push({ pathname: `/profile/password`, params: { companyId, next } })}
            errors={errors}
          />
          {confirmDelete && (
            <NavigationDelete
              entity={t('Profile')}
              visible={!!confirmDelete}
              onClose={() => setConfirmDelete(undefined)}
              onDelete={remove}
              protectEmail
            />
          )}
        </View>
      )}
    </View>
  )
}

export default ProfileDetail
